/**
 * The one thing this site asks a visitor to do, on its way to the server.
 *
 * A press is worth almost nothing on its own and everything in aggregate, so the wire rules in
 * `docs/api.md` § 1.1 are about volume: at most 20 clicks in a frame, at most one frame per
 * target per 200 ms, and a flush the moment 20 have piled up. That is 100 clicks/s from a tab
 * that is being hammered, which is deliberately more than the server will accept — see the note
 * about clipping below.
 *
 * Four failure modes shaped this file, and every one of them has cost some other client its
 * counter:
 *
 *   - **The socket is not open.** Either it never opened (pod at its cap, 503 at the upgrade, so
 *     the connection is in REST fallback) or it is between reconnects. Presses keep the queue,
 *     and the queue is capped at 200 per target: banking thousands of clicks to dump on reconnect
 *     is how a tab that was left open overnight turns into a burst the rate limiter eats anyway.
 *   - **The tab is going away.** `pagehide` is the last moment anything can be sent, and it is
 *     too late for `fetch`. The remaining clicks go out with `sendBeacon`, which the browser
 *     delivers after the page is gone.
 *   - **Clipping.** The server's budget is 25 clicks/s per socket (burst 50) and 100/s per
 *     address (burst 200), so a genuinely hammered tab will have batches accepted only in part —
 *     `ack.n` below what was sent, sometimes zero, and a batch clipped to nothing gets no ack at
 *     all. Those clicks are gone by design and this file never retries them. Retrying would turn
 *     a rate limit into a feedback loop that guarantees it is hit again.
 *   - **A missing ack.** Acks ride a droppable queue. Nothing here waits for one, no state is
 *     gated on one, and a batch is considered spent the moment it leaves.
 */

import {
  clickBody,
  clickFrame,
  parseAck,
  type Ack,
  type ClickTarget,
  type ClientMsg,
} from './transport'

/** `docs/api.md` § 1.1: at most one frame per 200 ms per target. */
export const FLUSH_MS = 200
/** `docs/api.md` § 1.1: `n` is clamped server-side to 1..20, so never send more. */
export const MAX_PER_MSG = 20
/** `docs/api.md` § 1.3: "queue ≤ 200 pending clicks"; older clicks are dropped. */
export const QUEUE_CAP = 200
/** A 429 from `POST /api/click` carries `Retry-After: 1`; this is the floor if it does not. */
const RETRY_AFTER_MS = 1_000
/** Two POSTs in flight is enough to cover latency; more is a way to hit the per-IP limiter. */
const MAX_IN_FLIGHT = 2

const TARGETS: readonly ClickTarget[] = ['openai', 'anthropic', 'google', 'reset']

export interface ClickBatcherOptions {
  /** The socket. Returns false when the frame did not go out, which is not an error. */
  send(msg: ClientMsg): boolean
  /** Acks that came back from the REST path; socket acks arrive through the connection instead. */
  onAck?(ack: Ack): void
  fetchImpl?: typeof fetch
  postUrl?: string
  /** Injected so a test can watch the beacon without a real page unload. */
  beacon?: (url: string, body: BodyInit) => boolean
  /** Injected so the `pagehide` listener can be exercised, and so tests can opt out of it. */
  target?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null
}

export interface ClickBatcher {
  /**
   * One press. Never throws, never blocks, never allocates on the common path.
   *
   * A property rather than a method so it can be handed straight to the store's click sink: it
   * closes over its batcher and has no `this` to lose.
   */
  press: (target: ClickTarget) => void
  /** Send what is queued now, subject to the 20-per-message cap. */
  flush(): void
  /** Clicks pressed but not yet sent — the queue, not the unacknowledged count. */
  pending(target: ClickTarget): number
  stop(): void
}

export function createClickBatcher(opts: ClickBatcherOptions): ClickBatcher {
  const postUrl = opts.postUrl ?? '/api/click'
  const doFetch = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : null)
  const beacon =
    opts.beacon ??
    (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
      ? navigator.sendBeacon.bind(navigator)
      : null)
  const listenTarget =
    opts.target === undefined ? (typeof window === 'undefined' ? null : window) : opts.target

  const pending: Record<ClickTarget, number> = { openai: 0, anthropic: 0, google: 0, reset: 0 }
  let timer: ReturnType<typeof setTimeout> | null = null
  let lastFlushAt = 0
  let inFlight = 0
  let restBlockedUntil = 0
  let stopped = false

  function arm(delayMs = FLUSH_MS): void {
    if (timer !== null || stopped) return
    timer = setTimeout(() => {
      timer = null
      flush()
    }, delayMs)
  }

  function queue(target: ClickTarget, n: number): void {
    // Clamp rather than drop-oldest: clicks are indistinguishable from one another, so "the
    // newest 200" and "the oldest 200" are the same 200 clicks.
    pending[target] = Math.min(QUEUE_CAP, pending[target] + n)
  }

  function post(target: ClickTarget, n: number): void {
    if (doFetch === null) {
      queue(target, n)
      return
    }
    inFlight++
    void doFetch(postUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: clickBody(target, n),
      keepalive: true,
    })
      .then(async (response) => {
        if (response.status === 429) {
          // The limiter said wait, so wait: the clicks go back on the queue and the REST path is
          // shut for a second. Hammering it instead would keep the limiter tripped indefinitely.
          const after = Number(response.headers.get('retry-after'))
          restBlockedUntil =
            Date.now() + (Number.isFinite(after) && after > 0 ? after * 1_000 : RETRY_AFTER_MS)
          queue(target, n)
          arm()
          return
        }
        if (!response.ok) return
        // Always a body, even when the whole batch was clipped: `{"t":"ack","n":0}` with a 200.
        const ack = parseAck(await response.json())
        if (ack !== null) opts.onAck?.(ack)
      })
      .catch(() => {
        // Offline, or the API is unreachable. These clicks were never sent, so they are still
        // owed; a clipped batch is the only kind that is deliberately forgotten.
        queue(target, n)
        arm()
      })
      .finally(() => {
        inFlight--
      })
  }

  function flush(): void {
    if (stopped) return
    lastFlushAt = Date.now()
    let remaining = false
    for (const target of TARGETS) {
      const waiting = pending[target]
      if (waiting === 0) continue
      const n = Math.min(waiting, MAX_PER_MSG)
      pending[target] = waiting - n

      if (opts.send(clickFrame(target, n))) {
        if (pending[target] > 0) remaining = true
        continue
      }
      if (inFlight < MAX_IN_FLIGHT && Date.now() >= restBlockedUntil) {
        post(target, n)
        if (pending[target] > 0) remaining = true
        continue
      }
      // Nowhere to put them: back on the queue, and try again on the next tick of the timer.
      queue(target, n)
      remaining = true
    }
    if (remaining) arm()
  }

  /**
   * The last chance. `fetch` is cancelled when the document goes away and the socket is closing
   * anyway, so anything still queued goes out as a beacon the browser delivers on our behalf.
   */
  function flushOnHide(): void {
    for (const target of TARGETS) {
      let waiting = pending[target]
      if (waiting === 0) continue
      pending[target] = 0
      while (waiting > 0) {
        const n = Math.min(waiting, MAX_PER_MSG)
        waiting -= n
        if (opts.send(clickFrame(target, n))) continue
        if (beacon === null) break
        // An explicit JSON blob: a string body would be sent as text/plain and the API would
        // answer 415 to a request nobody is left to read the answer of.
        beacon(postUrl, new Blob([clickBody(target, n)], { type: 'application/json' }))
      }
    }
  }

  listenTarget?.addEventListener('pagehide', flushOnHide)

  return {
    press(target: ClickTarget): void {
      if (stopped) return
      const next = Math.min(QUEUE_CAP, pending[target] + 1)
      pending[target] = next
      // The doc's own rule: flush at 200 ms *or* when n reaches 20, whichever comes first. The
      // two only disagree above 100 clicks/s, which no hand produces and a synthetic hammer
      // does; there, "at most every 200 ms" wins, so the cap flushes early but never early
      // enough to put more than five frames a second on the wire.
      if (next >= MAX_PER_MSG) {
        const since = Date.now() - lastFlushAt
        if (since >= FLUSH_MS) {
          if (timer !== null) {
            clearTimeout(timer)
            timer = null
          }
          flush()
          return
        }
        arm(FLUSH_MS - since)
        return
      }
      arm()
    },
    flush,
    pending(target: ClickTarget): number {
      return pending[target]
    },
    stop(): void {
      stopped = true
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      listenTarget?.removeEventListener('pagehide', flushOnHide)
    },
  }
}
